import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CustomPagination from '../../components/forms/Pagination';
import { OperationalDaysClient, OperationalDayTemplateDto } from '../../app/services/api/tasksClient';

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);

const operationalDaysClient = OperationalDaysClient;

const OperationalDaysPage: React.FC = () => {
  const { t } = useTranslation('operationalDays');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['OperationalDaysView']) : false;
  const canCreate = hasPermissionTo ? hasPermissionTo(['OperationalDaysCreate']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['OperationalDaysDelete']) : false;

  const [days, setDays] = useState<OperationalDayTemplateDto[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchActiveOnly, setSearchActiveOnly] = useState(true);

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [deleteTarget, setDeleteTarget] = useState<OperationalDayTemplateDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!canView) return;
    operationalDaysClient
      .getOperationalDays(searchActiveOnly ? true : undefined)
      .then(r => setDays((r).data ?? r))
      .catch(() => setDays([]));
  }, [canView, searchActiveOnly]);

  const filteredDays = useMemo(() => {
    return days.filter(d => {
      if (searchActiveOnly && !d.isActive) return false;
      if (searchText.trim()) {
        const text = searchText.toLowerCase();
        const name = (d.name ?? '').toLowerCase();
        const description = (d.description ?? '').toLowerCase();
        if (!name.includes(text) && !description.includes(text)) return false;
      }
      return true;
    });
  }, [days, searchActiveOnly, searchText]);

  const pagedDays = useMemo(
    () => filteredDays.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredDays, page, rowsPerPage],
  );

  const maxPages = Math.ceil(filteredDays.length / rowsPerPage);

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/operational-days/new');
  };

  const handleOpenEdit = (row: OperationalDayTemplateDto) => {
    if (!canView) return;
    navigate(`/operational-days/${row.id}`);
  };

  const handleRequestDelete = (row: OperationalDayTemplateDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDelete) return;
    setDeleteTarget(row);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await operationalDaysClient.deleteOperationalDay(deleteTarget.id);
      setDays(prev => prev.filter(d => d.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmOpen(false);
  };

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('listPageTitle', 'Operational Days')}</PageTitle>
        <Pill text={t('listNoPermission', 'You do not have permission to view operational days.')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle>{t('listPageTitle', 'Operational Days')}</PageTitle>
        </Grid>

        <Grid item
          xs={12}>
          <Grid container
            spacing={1}
            alignItems='center'>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                label={t('listSearchLabel', 'Search')}
                fullWidth
                size='small'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder={t('listSearchPlaceholder', 'Search by name or description')}
              />
            </Grid>
            <Grid item>
              <QuickFilterDivider />
            </Grid>
            <Grid item>
              <Chip
                label={t('listActiveOnlyFilter', 'Active only')}
                color={searchActiveOnly ? 'primary' : 'default'}
                onClick={() => setSearchActiveOnly(prev => !prev)}
              />
            </Grid>
            <Grid item
              xs
              sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {canCreate && (
                <Button
                  size='lg'
                  variant='primary'
                  startIcon={<Plus />}
                  onClick={handleOpenCreate}
                >
                  {t('listCreateButton', 'Create Operational Day')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {t('listTableTitle', 'Operational Days')}
              <Pill text={t('listTableCount', '{{count}} Operational Days', { count: filteredDays.length })} />
            </TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('listColumnName', 'Name')}</TableCell>
                    <TableCell>{t('listColumnDescription', 'Description')}</TableCell>
                    <TableCell>{t('listColumnEffectiveFrom', 'Effective from')}</TableCell>
                    <TableCell>{t('listColumnEffectiveTo', 'Effective to')}</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedDays.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell>{row.effectiveFromDate?.substring(0, 10)}</TableCell>
                      <TableCell>{row.effectiveToDate?.substring(0, 10)}</TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        {canDelete && (
                          <DeleteOutlineIcon
                            fontSize='small'
                            color='error'
                            onClick={e => handleRequestDelete(row, e)}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedDays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}
                        style={{ textAlign: 'center' }}>
                        {t('listEmptyState', 'No operational days found.')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
            />
          </Section>
        </Grid>
      </Grid>

      {deleteTarget && (
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>{t('listDeleteDialogTitle', 'Delete operational day?')}</DialogTitle>
          <DialogContent>
            {t('listDeleteDialogBody', 'Are you sure you want to delete the operational day "{{name}}"? This action cannot be undone.', { name: deleteTarget.name })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              {t('listDeleteDialogCancel', 'Cancel')}
            </Button>
            <Button
              variant='primary'
              color='error'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {t('listDeleteDialogConfirm', 'Delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </PageArea>
  );
};

export default OperationalDaysPage;
